import Keycloak from 'keycloak-js'
import { Config } from '@/services/ConfigService'
import AuthService from '@/services/Auth/AuthService'
import { store } from '@/store'
import { i18n } from '@/plugins/i18n'
const IDENTITY_PROVIDER_MODULE_NAME = 'identity-provider'

const instanceOptions = {
	url: Config.VUE_APP_KEYCLOAK_URL,
	realm: Config.VUE_APP_KEYCLOAK_REALM,
	clientId: Config.VUE_APP_KEYCLOAK_CLIENT_ID
}

const initOptions = {
	onLoad: 'check-sso',
	checkLoginIframe: false,
	pkceMethod: 'S256'
}

const _keycloak = new Keycloak(instanceOptions)

let refreshTokenIntervalId = null
let lastThemeData = null

function buildWelybThemeData() {
	const requestedTheme = localStorage.getItem('darkMode') ?? 'light'
	const whiteLabelData = store.state.whitelabel.list.find(theming => theming.theme === requestedTheme)
	const oauthProvidersData = store.state.oauthProviders.oauthProviders
	const forgotPasswordUrl = window.location.protocol + '//' + window.location.host + '/reset-password'

	if (!whiteLabelData) {
		return getFallbackThemeData()
	}

	return {
		theme: whiteLabelData?.theme || requestedTheme || 'dark',
		primary_color: whiteLabelData?.primary_color || '#1614b8',
		secondary_color: whiteLabelData?.secondary_color || '#750a18',
		logo: whiteLabelData?.logo || null,
		oauth_providers: normalizeOauthProviders(oauthProvidersData),
		forgot_password_url: forgotPasswordUrl,
		labels: {
			email: i18n.t('signin.fields.email') || 'Adresse email',
			password: i18n.t('signin.fields.password') || 'Mot de passe',
			forgot_password: i18n.t('signin.messages.forgotten_password') || 'Mot de passe oublié ?',
			login_title: i18n.t('signin.title') || 'Connectez vous',
			login_button: i18n.t('signin.messages.button') || 'Se connecter',
			login_error: i18n.t('signin.errors.failed_authentication') || 'Combinaison email et mot de passe invalide',
			support: i18n.t('support') || 'Support',
			field_mandatory: i18n.t('required') || 'Champ obligatoire'
		}
	}
}

function normalizeOauthProviders(oauthProvidersResponse) {
	const oauthProviders = extractOauthProviders(oauthProvidersResponse)

	return oauthProviders.filter(isActiveOauthProvider).map(normalizeOauthProvider)
}

function extractOauthProviders(oauthProvidersResponse) {
	if (Array.isArray(oauthProvidersResponse?.data)) {
		return oauthProvidersResponse.data
	}

	if (Array.isArray(oauthProvidersResponse)) {
		return oauthProvidersResponse
	}

	return []
}

function isActiveOauthProvider(provider) {
	return !!provider?.is_active
}

function normalizeOauthProvider(provider) {
	return {
		identifier: provider.identifier || provider.driver_name,
		name: provider.name,
		primary_color: provider.primary_color,
		logo: provider.signin_logo || provider.logo,
		link: provider.link
	}
}

function getFallbackThemeData() {
	return {
		name: 'Welyb',
		theme: 'dark',
		primary_color: '#60c080',
		secondary_color: '#F25267',
		logo: null,
		oauth_providers: [],
		labels: {
			email: i18n.t('signin.fields.email') || 'Adresse email',
			password: i18n.t('signin.fields.password') || 'Mot de passe',
			forgot_password: i18n.t('signin.messages.forgotten_password') || 'Mot de passe oublié ?',
			login_title: i18n.t('signin.title') || 'Connectez vous',
			login_button: i18n.t('signin.messages.button') || 'Se connecter',
			login_error: i18n.t('signin.errors.failed_authentication') || 'Combinaison email et mot de passe invalide',
			support: i18n.t('support') || 'Support',
			field_mandatory: i18n.t('required') || 'Champ obligatoire'
		}
	}
}

function base64UrlEncodeObject(value) {
	const json = JSON.stringify(value)

	const utf8Bytes = new TextEncoder().encode(json)

	let binary = ''

	utf8Bytes.forEach(byte => {
		binary += String.fromCharCode(byte)
	})

	return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function appendThemeParams(loginUrl, themeData) {
	const url = new URL(loginUrl)

	url.searchParams.set('welyb_theme_data', base64UrlEncodeObject(themeData))

	return url.toString()
}

async function loginWithTheme(themeData = getFallbackThemeData()) {
	lastThemeData = themeData

	const loginUrl = await _keycloak.createLoginUrl({
		redirectUri: window.location.href
	})

	window.location.href = appendThemeParams(loginUrl, themeData)
}

_keycloak.onAuthSuccess = () => {
	AuthService.setToken(`Bearer ${_keycloak.token}`)
	Private.refreshTokenLoop(10)
}

function redirectToKeycloakLogin() {
	const identityProviderModuleIsActive = store.state.modules.list.some(module => module.name === IDENTITY_PROVIDER_MODULE_NAME)
	if (identityProviderModuleIsActive) {
		const themeData = buildWelybThemeData()
		return loginWithTheme(themeData)
	} else {
		return
	}
}

const Private = {
	refreshTokenLoop: (intervalSeconds = 60) => {
		if (refreshTokenIntervalId) {
			clearInterval(refreshTokenIntervalId)
		}

		refreshTokenIntervalId = setInterval(() => {
			_keycloak
				.updateToken(30)
				.then(() => {
					AuthService.setToken(`Bearer ${_keycloak.token}`)
				})
				.catch(() => {
					loginWithTheme(lastThemeData || getFallbackThemeData())
				})
		}, intervalSeconds * 1000)
	}
}

export default {
	init: (options = {}) => {
		return _keycloak.init({
			...initOptions,
			...options,
			onLoad: options.onLoad || initOptions.onLoad
		})
	},

	login: () => {
		const themeData = buildWelybThemeData()
		return loginWithTheme(themeData)
	},

	logout: () => _keycloak.logout(),

	isAuthenticated: () => _keycloak.authenticated,

	getToken: () => {
		return _keycloak ? _keycloak.token : null
	},

	updateToken: minValidity => _keycloak.updateToken(minValidity),

	redirectToKeycloakLogin: () => {
		redirectToKeycloakLogin()
	}
}
